/**
 * Generates the HTML content for a comprehensive project data export.
 * 
 * @param {Object} project - The project data object.
 * @param {Object} currentOrganization - The current organization data object.
 * @param {Object} user - The current user object (for "Exported By").
 * @param {Object} stats - Calculated statistics keys: { totalTasks, completedTasks, progress, teamSize }.
 * @param {Array} enrichedTasks - List of tasks with expanded assignee details.
 * @param {Array} teamMembers - List of team members.
 * @returns {string} The complete HTML string for the export file.
 */
export const generateProjectExportHtml = ({
  project,
  currentOrganization,
  user,
  stats,
  enrichedTasks,
  teamMembers
}) => {
  const exportDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
  });

  const { totalTasks, completedTasks, progress, teamSize } = stats;

  // Sort tasks by status to group them together
  const sortedTasks = [...enrichedTasks].sort((a, b) => {
    const statusA = (a.status || '').toLowerCase();
    const statusB = (b.status || '').toLowerCase();
    return statusA.localeCompare(statusB);
  });

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <title>Project Data Export - ${project?.name}</title>
      <style>
        :root {
          --primary: #2563eb;
          --secondary: #64748b;
          --success: #10b981;
          --warning: #f59e0b;
          --danger: #ef4444;
          --light: #f8fafc;
          --border: #e2e8f0;
        }
        body { 
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
          line-height: 1.5; 
          color: #1e293b;
          max-width: 1100px; 
          margin: 0 auto; 
          padding: 40px; 
          background: #fff;
        }
        @media print {
          body { padding: 0; max-width: 100%; }
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
          footer { position: fixed; bottom: 0; width: 100%; }
        }
        
        .header-container {
          border-bottom: 2px solid var(--border);
          padding-bottom: 24px;
          margin-bottom: 32px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        .project-title { font-size: 32px; font-weight: 800; color: #0f172a; margin: 0; }
        .org-name { font-size: 14px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--secondary); margin-bottom: 8px; }
        
        .print-btn {
          background: var(--primary);
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
          transition: all 0.2s;
        }
        .print-btn:hover { background: #1d4ed8; transform: translateY(-1px); }

        section { margin-bottom: 48px; }
        h2 { font-size: 20px; font-weight: 700; color: #1e293b; border-bottom: 1px solid var(--border); padding-bottom: 8px; margin-bottom: 20px; }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }
        .stat-card {
          background: var(--light);
          padding: 20px;
          border-radius: 12px;
          border: 1px solid var(--border);
        }
        .stat-value { font-size: 24px; font-weight: 700; color: var(--primary); }
        .stat-label { font-size: 12px; color: var(--secondary); font-weight: 600; text-transform: uppercase; }

        .progress-bar {
          height: 12px;
          background: #f1f5f9;
          border-radius: 6px;
          overflow: hidden;
          margin: 16px 0;
        }
        .progress-fill { height: 100%; background: var(--success); }

        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th { background: #f8fafc; text-align: left; padding: 12px 16px; font-size: 13px; font-weight: 600; color: #475569; border-bottom: 1px solid var(--border); }
        td { padding: 12px 16px; font-size: 14px; border-bottom: 1px solid var(--border); color: #334155; }
        
        .badge {
          padding: 2px 8px;
          border-radius: 9999px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }
        .badge-low { background: #f0fdf4; color: #166534; }
        .badge-medium { background: #fffbeb; color: #92400e; }
        .badge-high { background: #fef2f2; color: #991b1b; }
        
        .metadata-item { margin-bottom: 8px; font-size: 14px; }
        .metadata-label { font-weight: 600; color: var(--secondary); width: 120px; display: inline-block; }

        footer {
          margin-top: 64px;
          padding-top: 16px;
          border-top: 1px solid var(--border);
          text-align: center;
          font-size: 12px;
          color: var(--secondary);
        }
      </style>
    </head>
    <body>
      <div class="no-print" style="text-align: right; margin-bottom: 20px;">
        <button onclick="window.print()" class="print-btn">Download as PDF / Print</button>
        <p style="font-size: 12px; color: var(--secondary); margin-top: 8px;">Select "Save as PDF" in the print destination</p>
      </div>

      <div class="header-container">
        <div>
          <div class="org-name">${currentOrganization?.name || 'Organization Project'}</div>
          <h1 class="project-title">${project?.name}</h1>
          <p style="color: var(--secondary); max-width: 600px; margin-top: 8px;">${project?.description || 'Comprehensive Project Data Export'}</p>
        </div>
        <div style="text-align: right;">
          <div class="metadata-item"><span class="metadata-label">Export Date:</span> ${exportDate}</div>
          <div class="metadata-item"><span class="metadata-label">Exported By:</span> ${user?.displayName || user?.email}</div>
          <div class="metadata-item"><span class="metadata-label">Status:</span> <strong>${project?.status?.toUpperCase()}</strong></div>
        </div>
      </div>

      <section>
        <h2>Project Performance Overview</h2>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">${totalTasks}</div>
            <div class="stat-label">Total Tasks</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${completedTasks}</div>
            <div class="stat-label">Completed</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${progress}%</div>
            <div class="stat-label">Progress Rate</div>
          </div>
          <div class="stat-card">
            <div class="stat-value">${teamSize}</div>
            <div class="stat-label">Team Members</div>
          </div>
        </div>
        <div style="font-size: 14px; font-weight: 600; margin-bottom: 4px;">Overall Completion Progress</div>
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${progress}%"></div>
        </div>
      </section>

      <section>
        <h2>Task Directory</h2>
        <table>
          <thead>
            <tr>
              <th>Task Title</th>
              <th>Assignee</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Due Date</th>
            </tr>
          </thead>
          <tbody>
            ${sortedTasks.length > 0 ? sortedTasks.map(task => `
              <tr>
                <td style="font-weight: 500;">${task.title}</td>
                <td>${task.assigneeDetails?.length > 0 
                  ? task.assigneeDetails.map(u => `${u.first_name} ${u.last_name}`).join(', ') 
                  : 'Unassigned'}</td>
                <td><span class="badge badge-${task.priority?.toLowerCase() || 'medium'}">${task.priority || 'Medium'}</span></td>
                <td><span style="font-weight: 600;">${task.status?.replace('_', ' ')}</span></td>
                <td>${task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No date'}</td>
              </tr>
            `).join('') : '<tr><td colspan="5" style="text-align: center; padding: 32px; color: var(--secondary);">No tasks found in this project.</td></tr>'}
          </tbody>
        </table>
      </section>

      <section class="page-break">
        <h2>Team Directory</h2>
        <table>
          <thead>
            <tr>
              <th>Member Name</th>
              <th>Email Address</th>
              <th>Project Role</th>
            </tr>
          </thead>
          <tbody>
            ${teamMembers.length > 0 ? teamMembers.map(member => `
              <tr>
                <td style="font-weight: 600;">${member.first_name} ${member.last_name}</td>
                <td>${member.email}</td>
                <td>${member.role?.[0]?.toUpperCase() + member.role?.slice(1) || 'Member'}</td>
              </tr>
            `).join('') : '<tr><td colspan="3" style="text-align: center; padding: 32px; color: var(--secondary);">No team members listed.</td></tr>'}
          </tbody>
        </table>
      </section>

      <footer>
        <div style="margin-bottom: 8px;">Generated by Agno WorkSphere &bull; Comprehensive Project Intelligence</div>
        <div>&copy; ${new Date().getFullYear()} ${currentOrganization?.name || 'Agno WorkSphere'}. All rights reserved.</div>
      </footer>
    </body>
    </html>
  `;
};
